import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Pagination from '../../components/common/Pagination';
import AdvancedSearch, { SearchField, SearchCondition, conditionsToQueryParams, queryParamsToConditions } from '../../components/common/AdvancedSearch';
import ProductCard, { ConstructionProduct } from '../../components/constructions/ProductCard';
import Toast from '../../components/common/Toast';
import { exportToCSV, importCSVFile, parseCSV } from '../../utils/csv';
import { IncentiveSettings } from '../../types';

// CSVインポート用の行データ型（全項目）
interface CSVImportRow {
  // 基本情報
  constructionNumber?: string;
  title?: string;
  description?: string;
  companyId?: string;
  companyName?: string;
  areaId?: string; // エリアマスタID（旧groupId）
  groupId?: string; // グループID
  constructionTypeId?: string;
  contractTypeId?: string;
  status?: string;
  priority?: string;
  assignedTo?: string;
  assignedToName?: string;
  // 金額情報
  estimateAmount?: string;
  orderAmount?: string;
  referralFee?: string;
  referralFeePercentage?: string;
  // 日程情報
  orderDate?: string; // 受注日
  constructionDate?: string; // 施工日
  desiredDate?: string; // 工事希望日
  completionDate?: string;
  contractDate?: string; // 契約日
  startDate?: string; // 開始日
  endDate?: string; // 終了日
  // 需要場所情報
  demandLocationId?: string;
  demandLocationName?: string;
  demandLocationAddress?: string;
  demandLocationPostalCode?: string;
  demandLocationContact?: string;
  demandLocationPhone?: string;
  // 電力会社情報
  powerCompanyId?: string;
  powerCompanyName?: string;
  powerCompanyBranch?: string;
  customerNumber?: string;
  supplyNumber?: string;
  // 施工業者情報
  constructionPartnerId?: string;
  // 請求情報
  priorBilling?: string; // 前請求の有無
  billingDestination?: string; // 請求先
  invoiceNumber?: string;
  invoiceDate?: string;
  invoiceAmount?: string;
  invoiceStatus?: string;
  paymentDate?: string;
  // インセンティブ情報（旧）
  incentiveAmount?: string;
  incentivePaid?: string;
  incentivePaidDate?: string;
  // 担当者インセンティブ
  assignedToIncentiveRate?: string;
  assignedToIncentiveAmount?: string;
  // PMインセンティブ
  pmUserId?: string;
  pmUserName?: string;
  pmIncentiveRate?: string;
  pmIncentiveAmount?: string;
  // プロモーターインセンティブ
  promoterUserId?: string;
  promoterUserName?: string;
  promoterIncentiveRate?: string;
  promoterIncentiveAmount?: string;
  // 製品情報
  products?: string;
}

// CSVインポート用のカラムマッピング（全項目）
const csvColumnMapping: Record<string, keyof CSVImportRow> = {
  // 基本情報
  '工事番号': 'constructionNumber',
  'タイトル': 'title',
  '説明': 'description',
  '企業ID': 'companyId',
  '企業名': 'companyName',
  'エリアID': 'areaId',
  '工事種別ID': 'constructionTypeId',
  '契約種別ID': 'contractTypeId',
  'ステータス': 'status',
  '優先度': 'priority',
  '担当者ID': 'assignedTo',
  '担当者': 'assignedToName',
  // 金額情報
  '見積金額': 'estimateAmount',
  '受注金額': 'orderAmount',
  '紹介手数料': 'referralFee',
  '紹介手数料率': 'referralFeePercentage',
  // 日程情報
  '受注日': 'orderDate',
  '施工日': 'constructionDate',
  '工事希望日': 'desiredDate',
  '完了日': 'completionDate',
  // 需要場所情報
  '需要場所ID': 'demandLocationId',
  '需要場所': 'demandLocationName',
  '需要場所住所': 'demandLocationAddress',
  '需要場所郵便番号': 'demandLocationPostalCode',
  '需要場所担当者': 'demandLocationContact',
  '需要場所電話番号': 'demandLocationPhone',
  // 電力会社情報
  '電力会社ID': 'powerCompanyId',
  '電力会社': 'powerCompanyName',
  '電力会社支店': 'powerCompanyBranch',
  'お客様番号': 'customerNumber',
  '供給地点番号': 'supplyNumber',
  // 施工業者情報
  '施工パートナーID': 'constructionPartnerId',
  // 請求情報
  '前請求の有無': 'priorBilling',
  '請求先': 'billingDestination',
  '請求書番号': 'invoiceNumber',
  '請求日': 'invoiceDate',
  '請求金額': 'invoiceAmount',
  '請求ステータス': 'invoiceStatus',
  '入金日': 'paymentDate',
  // インセンティブ情報（旧）
  'インセンティブ金額': 'incentiveAmount',
  'インセンティブ支払済': 'incentivePaid',
  'インセンティブ支払日': 'incentivePaidDate',
  // 担当者インセンティブ
  '担当者インセンティブ率': 'assignedToIncentiveRate',
  '担当者インセンティブ額': 'assignedToIncentiveAmount',
  // PMインセンティブ
  'PM ID': 'pmUserId',
  'PM': 'pmUserName',
  'PMインセンティブ率': 'pmIncentiveRate',
  'PMインセンティブ額': 'pmIncentiveAmount',
  // プロモーターインセンティブ
  'プロモーターID': 'promoterUserId',
  'プロモーター': 'promoterUserName',
  'プロモーターインセンティブ率': 'promoterIncentiveRate',
  'プロモーターインセンティブ額': 'promoterIncentiveAmount',
  // 製品情報
  '製品情報': 'products',
};

// ステータスの逆マッピング
const statusReverseMapping: Record<string, string> = {
  '準備中': 'pending',
  '進行中': 'in_progress',
  '完了': 'completed',
  'キャンセル': 'cancelled',
};

// 優先度の逆マッピング
const priorityReverseMapping: Record<string, string> = {
  '低': 'low',
  '通常': 'normal',
  '高': 'high',
  '緊急': 'urgent',
};

// 請求ステータスの逆マッピング
const invoiceStatusReverseMapping: Record<string, string> = {
  '未発行': 'not_issued',
  '発行済': 'sent',
  '入金済': 'paid',
};

interface Construction {
  id: string;
  constructionNumber: string;
  title: string;
  description?: string;
  companyId?: string;
  companyName?: string;
  areaId?: string; // エリアマスタID
  areaName?: string; // エリア名（JOIN）
  constructionTypeId?: string;
  contractTypeId?: string;
  status: string;
  priority: string;
  assignedTo?: string;
  assignedToName?: string;
  // 金額情報
  estimateAmount?: string;
  orderAmount?: string;
  referralFee?: string;
  referralFeePercentage?: string;
  // 日程情報
  orderDate?: string; // 受注日
  constructionDate?: string; // 施工日
  desiredDate?: string; // 工事希望日
  completionDate?: string;
  // 需要場所情報
  demandLocationId?: string;
  demandLocationName?: string;
  demandLocationAddress?: string;
  demandLocationPostalCode?: string;
  demandLocationContact?: string;
  demandLocationPhone?: string;
  // 電力会社情報
  powerCompanyId?: string;
  powerCompanyName?: string;
  powerCompanyBranch?: string;
  customerNumber?: string;
  supplyNumber?: string;
  // 施工業者情報
  constructionPartnerId?: string;
  constructionPartnerName?: string; // 施工パートナー名（JOIN）
  // 請求情報
  priorBilling?: string; // 前請求の有無
  billingDestination?: string; // 請求先
  invoiceNumber?: string;
  invoiceDate?: string;
  invoiceAmount?: string;
  invoiceStatus?: string;
  paymentDate?: string;
  // インセンティブ情報（旧）
  incentiveAmount?: string;
  incentivePaid?: boolean;
  incentivePaidDate?: string;
  // 担当者インセンティブ
  assignedToIncentiveRate?: string;
  assignedToIncentiveAmount?: string;
  // PMインセンティブ
  pmUserId?: string;
  pmUserName?: string;
  pmIncentiveRate?: string;
  pmIncentiveAmount?: string;
  // プロモーターインセンティブ
  promoterUserId?: string;
  promoterUserName?: string;
  promoterIncentiveRate?: string;
  promoterIncentiveAmount?: string;
  // 製品情報
  products?: unknown;
  // システム情報
  createdAt: string;
}

interface PaginatedResponse {
  items: Construction[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface Company {
  id: string;
  name: string;
}

interface Area {
  id: string;
  name: string;
  code?: string;
}

interface User {
  id: string;
  name: string;
}

interface MasterItem {
  id: string;
  name: string;
  code?: string;
  postalCode?: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  model?: string;
  unitPrice?: number;
}

const initialFormData = {
  // 基本情報
  title: '',
  description: '',
  companyId: '',
  areaId: '', // エリアマスタID
  constructionTypeId: '',
  contractTypeId: '',
  status: 'pending',
  priority: 'normal',
  assignedTo: '',
  // 金額情報
  estimateAmount: '',
  orderAmount: '',
  referralFee: '',
  referralFeePercentage: '',
  // 日程情報
  orderDate: '', // 受注日
  constructionDate: '', // 施工日
  desiredDate: '', // 工事希望日
  // 需要場所情報
  demandLocationId: '',
  demandLocationName: '',
  demandLocationAddress: '',
  demandLocationPostalCode: '',
  demandLocationContact: '',
  demandLocationPhone: '',
  // 電力会社情報
  powerCompanyId: '',
  powerCompanyName: '',
  powerCompanyBranch: '',
  customerNumber: '',
  supplyNumber: '',
  // 施工業者情報
  constructionPartnerId: '',
  // 請求情報
  priorBilling: '', // 前請求の有無
  billingDestination: '', // 請求先
  invoiceNumber: '',
  invoiceDate: '',
  invoiceAmount: '',
  invoiceStatus: 'not_issued',
  paymentDate: '',
  // インセンティブ情報(旧)
  incentiveAmount: '',
  incentivePaid: false,
  incentivePaidDate: '',
  // インセンティブ情報(新: 複数人振分)
  pmUserId: '',
  promoterUserId: '',
  assignedToIncentiveRate: '',
  pmIncentiveRate: '',
  promoterIncentiveRate: '',
};

export default function ConstructionList() {
  const { user } = useAuth();
  const { canCreate } = usePermission();
  const isAdmin = user?.roleCode === 'admin';
  const isPromoter = user?.roleCode === 'promoter' || isAdmin;
  const canCreateConstruction = canCreate('constructions');
  
  const [searchParams, setSearchParams] = useSearchParams();
  const quickSearch = searchParams.get('search') || '';
  const [constructions, setConstructions] = useState<Construction[]>([]);
  const [loading, setLoading] = useState(true);
  const isFirstLoadRef = useRef(true);
  const [searchConditions, setSearchConditions] = useState<SearchCondition[]>(() => {
    const saved = searchParams.get('conditions');
    return saved ? queryParamsToConditions(saved) : [];
  });
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'createdAt',
    direction: 'desc',
  });
  const [activeTab, setActiveTab] = useState<'basic' | 'demand' | 'power' | 'invoice' | 'product' | 'incentive'>('basic');
  
  // インセンティブ設定（管理者・プロモーターのみ）
  const [incentiveSettings, setIncentiveSettings] = useState<IncentiveSettings>({
    staffRate: 0.04,
    pmRate: 0.01,
    promoterRate: 0.02,
  });
  
  // マスターデータ
  const [companies, setCompanies] = useState<Company[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [constructionTypes, setConstructionTypes] = useState<MasterItem[]>([]);
  const [contractTypes, setContractTypes] = useState<MasterItem[]>([]);
  const [demandLocations, setDemandLocations] = useState<MasterItem[]>([]);
  const [powerCompanies, setPowerCompanies] = useState<MasterItem[]>([]);
  const [constructionPartners, setConstructionPartners] = useState<MasterItem[]>([]);
  const [products, setProducts] = useState<MasterItem[]>([]);
  
  // Toast通知
  const [toast, setToast] = useState<{message: string; type: 'success' | 'error'} | null>(null);

  // 製品情報（複数選択）
  const [formProducts, setFormProducts] = useState<ConstructionProduct[]>([]);
  const [selectedProductId, setSelectedProductId] = useState('');

  // 詳細検索フィールド定義
  const searchFields: SearchField[] = useMemo(() => [
    { key: 'constructionNumber', label: '工事番号', type: 'text', placeholder: 'CON-2024-001' },
    { key: 'title', label: 'タイトル', type: 'text', placeholder: 'タイトルを検索' },
    { key: 'companyId', label: '企業', type: 'select', options: companies.map(c => ({ value: c.id, label: c.name })) },
    { key: 'status', label: 'ステータス', type: 'select', options: [
      { value: 'pending', label: '準備中' },
      { value: 'in_progress', label: '進行中' },
      { value: 'completed', label: '完了' },
      { value: 'cancelled', label: 'キャンセル' },
    ]},
    { key: 'priority', label: '優先度', type: 'select', options: [
      { value: 'low', label: '低' },
      { value: 'normal', label: '通常' },
      { value: 'high', label: '高' },
      { value: 'urgent', label: '緊急' },
    ]},
    { key: 'estimateAmount', label: '見積金額', type: 'number', placeholder: '金額' },
    { key: 'orderAmount', label: '受注金額', type: 'number', placeholder: '金額' },
    { key: 'orderDate', label: '受注日', type: 'date' },
    { key: 'constructionDate', label: '施工日', type: 'date' },
    { key: 'desiredDate', label: '工事希望日', type: 'date' },
    { key: 'assignedTo', label: '担当者', type: 'select', options: users.map(u => ({ value: u.id, label: u.name })) },
    { key: 'demandLocationName', label: '需要場所', type: 'text', placeholder: '需要場所名' },
    { key: 'powerCompanyName', label: '電力会社', type: 'text', placeholder: '電力会社名' },
    { key: 'invoiceStatus', label: '請求ステータス', type: 'select', options: [
      { value: 'not_issued', label: '未発行' },
      { value: 'sent', label: '発行済' },
      { value: 'paid', label: '入金済' },
    ]},
  ], [companies, users]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (isFirstLoadRef.current) setLoading(true);
      try {
        const params = new URLSearchParams();
        if (quickSearch) {
          params.append('search', quickSearch);
        }
        if (searchConditions.length > 0) {
          params.append('advancedSearch', JSON.stringify(searchConditions));
        }
        params.append('page', currentPage.toString());
        params.append('pageSize', pageSize.toString());
        params.append('sortBy', sortConfig.key);
        params.append('sortOrder', sortConfig.direction);
        const data = await api.get<PaginatedResponse>(`/api/constructions?${params}`);
        if (cancelled) return;
        if (data?.items) {
          data.items.forEach((item, index) => {
            if (!item || !item.id) {
              console.warn(`[ConstructionList] Invalid item at index ${index}:`, item);
            }
          });
        }
        const validItems = Array.isArray(data?.items) 
          ? data.items.filter((item): item is Construction => item !== null && item !== undefined && typeof item.id === 'string')
          : [];
        setConstructions(validItems);
        setTotalPages(data?.totalPages ?? 1);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch constructions:', error);
        setConstructions([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          isFirstLoadRef.current = false;
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [searchConditions, currentPage, pageSize, sortConfig, quickSearch]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [
          companiesData,
          areasData,
          usersData,
          constructionTypesData,
          contractTypesData,
          demandLocationsData,
          powerCompaniesData,
          constructionPartnersData,
          productsData,
        ] = await Promise.all([
          api.get<{ items: Company[] }>('/api/companies?status=active'),
          api.get<{ items: Area[] }>('/api/masters/areas'),
          api.get<{ items: User[] }>('/api/users?status=active'),
          api.get<{ items: MasterItem[] }>('/api/masters/constructionTypes'),
          api.get<{ items: MasterItem[] }>('/api/masters/contractTypes'),
          api.get<{ items: MasterItem[] }>('/api/masters/demandLocations'),
          api.get<{ items: MasterItem[] }>('/api/masters/powerCompanies'),
          api.get<{ items: MasterItem[] }>('/api/masters/constructionPartners'),
          api.get<{ items: MasterItem[] }>('/api/masters/products'),
        ]);
        if (cancelled) return;
        setCompanies(Array.isArray(companiesData?.items) ? companiesData.items : []);
        setAreas(Array.isArray(areasData?.items) ? areasData.items : []);
        setUsers(Array.isArray(usersData?.items) ? usersData.items : []);
        setConstructionTypes(Array.isArray(constructionTypesData?.items) ? constructionTypesData.items : []);
        setContractTypes(Array.isArray(contractTypesData?.items) ? contractTypesData.items : []);
        setDemandLocations(Array.isArray(demandLocationsData?.items) ? demandLocationsData.items : []);
        setPowerCompanies(Array.isArray(powerCompaniesData?.items) ? powerCompaniesData.items : []);
        setConstructionPartners(Array.isArray(constructionPartnersData?.items) ? constructionPartnersData.items : []);
        setProducts(Array.isArray(productsData?.items) ? productsData.items : []);
        if (isPromoter) {
          try {
            const incentiveData = await api.get<IncentiveSettings>('/api/settings/incentive-rates');
            if (cancelled) return;
            setIncentiveSettings(incentiveData);
          } catch {
            // デフォルト値を使用
          }
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch master data:', error);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // 検索条件をURLに保存（page/pageSizeを維持）
  useEffect(() => {
    setSearchParams(prev => {
      if (searchConditions.length > 0) {
        prev.set('conditions', conditionsToQueryParams(searchConditions));
      } else {
        prev.delete('conditions');
      }
      return prev;
    }, { replace: true });
  }, [searchConditions, setSearchParams]);

  const fetchMasterData = async () => {
    try {
      const [
        companiesData,
        areasData,
        usersData,
        constructionTypesData,
        contractTypesData,
        demandLocationsData,
        powerCompaniesData,
        constructionPartnersData,
        productsData,
      ] = await Promise.all([
        api.get<{ items: Company[] }>('/api/companies?status=active'),
        api.get<{ items: Area[] }>('/api/masters/areas'),
        api.get<{ items: User[] }>('/api/users?status=active'),
        api.get<{ items: MasterItem[] }>('/api/masters/constructionTypes'),
        api.get<{ items: MasterItem[] }>('/api/masters/contractTypes'),
        api.get<{ items: MasterItem[] }>('/api/masters/demandLocations'),
        api.get<{ items: MasterItem[] }>('/api/masters/powerCompanies'),
        api.get<{ items: MasterItem[] }>('/api/masters/constructionPartners'),
        api.get<{ items: MasterItem[] }>('/api/masters/products'),
      ]);
      setCompanies(Array.isArray(companiesData?.items) ? companiesData.items : []);
      setAreas(Array.isArray(areasData?.items) ? areasData.items : []);
      setUsers(Array.isArray(usersData?.items) ? usersData.items : []);
      setConstructionTypes(Array.isArray(constructionTypesData?.items) ? constructionTypesData.items : []);
      setContractTypes(Array.isArray(contractTypesData?.items) ? contractTypesData.items : []);
      setDemandLocations(Array.isArray(demandLocationsData?.items) ? demandLocationsData.items : []);
      setPowerCompanies(Array.isArray(powerCompaniesData?.items) ? powerCompaniesData.items : []);
      setConstructionPartners(Array.isArray(constructionPartnersData?.items) ? constructionPartnersData.items : []);
      setProducts(Array.isArray(productsData?.items) ? productsData.items : []);
      
      // インセンティブ設定を取得（管理者・プロモーターのみ）
      if (isPromoter) {
        try {
          const incentiveData = await api.get<IncentiveSettings>('/api/settings/incentive-rates');
          setIncentiveSettings(incentiveData);
        } catch {
          // デフォルト値を使用
        }
      }
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    }
  };

  const fetchConstructions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // 詳細検索条件を追加
      if (searchConditions.length > 0) {
        params.append('advancedSearch', JSON.stringify(searchConditions));
      }
      
      params.append('page', currentPage.toString());
      params.append('pageSize', pageSize.toString());
      params.append('sortBy', sortConfig.key);
      params.append('sortOrder', sortConfig.direction);
      const data = await api.get<PaginatedResponse>(`/api/constructions?${params}`);
      
      // デバッグ: データの検証
      if (data?.items) {
        data.items.forEach((item, index) => {
          if (!item || !item.id) {
            console.warn(`[ConstructionList] Invalid item at index ${index}:`, item);
          }
        });
      }
      
      const validItems = Array.isArray(data?.items) 
        ? data.items.filter((item): item is Construction => item !== null && item !== undefined && typeof item.id === 'string')
        : [];
      setConstructions(validItems);
      setTotalPages(data?.totalPages ?? 1);
    } catch (error) {
      console.error('Failed to fetch constructions:', error);
      setConstructions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (conditions: SearchCondition[]) => {
    setSearchConditions(conditions);
    setSearchParams(prev => {
      prev.delete('page');
      return prev;
    });
  };

  const defaultSort = { key: 'createdAt', direction: 'desc' as const };
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return defaultSort;
    });
  };

  const [exporting, setExporting] = useState(false);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // 検索条件に一致する全件を取得
      const params = new URLSearchParams();
      if (searchConditions.length > 0) {
        params.append('advancedSearch', JSON.stringify(searchConditions));
      }
      params.append('page', '1');
      params.append('pageSize', '10000'); // 全件取得
      params.append('sortBy', sortConfig.key);
      params.append('sortOrder', sortConfig.direction);
      
      const data = await api.get<PaginatedResponse>(`/api/constructions?${params}`);
      const allItems = Array.isArray(data?.items) ? data.items : [];
      
      if (allItems.length === 0) {
        setToast({message: 'エクスポートするデータがありません', type: 'error'});
        return;
      }

      const csvColumns: Array<{ key: string; header: string; format?: (v: unknown) => string }> = [
        // 基本情報
        { key: 'constructionNumber', header: '工事番号' },
        { key: 'title', header: 'タイトル' },
        { key: 'description', header: '説明' },
        { key: 'companyId', header: '企業ID' },
        { key: 'companyName', header: '企業名' },
        { key: 'areaId', header: 'エリアID' },
        { key: 'areaName', header: 'エリア名' },
        { key: 'constructionTypeId', header: '工事種別ID' },
        { key: 'contractTypeId', header: '契約種別ID' },
        { key: 'status', header: 'ステータス', format: (v: unknown) => {
          const labels: Record<string, string> = { pending: '準備中', in_progress: '進行中', completed: '完了', cancelled: 'キャンセル' };
          return labels[v as string] || String(v);
        }},
        { key: 'priority', header: '優先度', format: (v: unknown) => {
          const labels: Record<string, string> = { low: '低', normal: '通常', high: '高', urgent: '緊急' };
          return labels[v as string] || String(v);
        }},
        { key: 'assignedTo', header: '担当者ID' },
        { key: 'assignedToName', header: '担当者' },
        // 金額情報
        { key: 'estimateAmount', header: '見積金額', format: (v: unknown) => v ? Number(v).toLocaleString() : '' },
        { key: 'orderAmount', header: '受注金額', format: (v: unknown) => v ? Number(v).toLocaleString() : '' },
        { key: 'referralFee', header: '紹介手数料', format: (v: unknown) => v ? Number(v).toLocaleString() : '' },
        { key: 'referralFeePercentage', header: '紹介手数料率' },
        // 日程情報
        { key: 'orderDate', header: '受注日', format: (v: unknown) => v ? new Date(v as string).toLocaleDateString('ja-JP') : '' },
        { key: 'constructionDate', header: '施工日', format: (v: unknown) => v ? new Date(v as string).toLocaleDateString('ja-JP') : '' },
        { key: 'desiredDate', header: '工事希望日', format: (v: unknown) => v ? new Date(v as string).toLocaleDateString('ja-JP') : '' },
        { key: 'completionDate', header: '完了日', format: (v: unknown) => v ? new Date(v as string).toLocaleDateString('ja-JP') : '' },
        // 需要場所情報
        { key: 'demandLocationId', header: '需要場所ID' },
        { key: 'demandLocationName', header: '需要場所' },
        { key: 'demandLocationAddress', header: '需要場所住所' },
        { key: 'demandLocationPostalCode', header: '需要場所郵便番号' },
        { key: 'demandLocationContact', header: '需要場所担当者' },
        { key: 'demandLocationPhone', header: '需要場所電話番号' },
        // 電力会社情報
        { key: 'powerCompanyId', header: '電力会社ID' },
        { key: 'powerCompanyName', header: '電力会社' },
        { key: 'powerCompanyBranch', header: '電力会社支店' },
        { key: 'customerNumber', header: 'お客様番号' },
        { key: 'supplyNumber', header: '供給地点番号' },
        // 施工パートナー情報
        { key: 'constructionPartnerId', header: '施工パートナーID' },
        { key: 'constructionPartnerName', header: '施工パートナー名' },
        // 請求情報
        { key: 'priorBilling', header: '前請求の有無' },
        { key: 'billingDestination', header: '請求先' },
        { key: 'invoiceNumber', header: '請求書番号' },
        { key: 'invoiceDate', header: '請求日', format: (v: unknown) => v ? new Date(v as string).toLocaleDateString('ja-JP') : '' },
        { key: 'invoiceAmount', header: '請求金額', format: (v: unknown) => v ? Number(v).toLocaleString() : '' },
        { key: 'invoiceStatus', header: '請求ステータス', format: (v: unknown) => {
          const labels: Record<string, string> = { not_issued: '未発行', issued: '発行済', paid: '入金済' };
          return labels[v as string] || String(v);
        }},
        { key: 'paymentDate', header: '入金日', format: (v: unknown) => v ? new Date(v as string).toLocaleDateString('ja-JP') : '' },
        // インセンティブ情報（旧）
        { key: 'incentiveAmount', header: 'インセンティブ金額', format: (v: unknown) => v ? Number(v).toLocaleString() : '' },
        { key: 'incentivePaid', header: 'インセンティブ支払済', format: (v: unknown) => v ? 'はい' : 'いいえ' },
        { key: 'incentivePaidDate', header: 'インセンティブ支払日', format: (v: unknown) => v ? new Date(v as string).toLocaleDateString('ja-JP') : '' },
        // 担当者インセンティブ
        { key: 'assignedToIncentiveRate', header: '担当者インセンティブ率', format: (v: unknown) => v ? `${(Number(v) * 100).toFixed(2)}%` : '' },
        { key: 'assignedToIncentiveAmount', header: '担当者インセンティブ額', format: (v: unknown) => v ? Number(v).toLocaleString() : '' },
        // PMインセンティブ
        { key: 'pmUserId', header: 'PM ID' },
        { key: 'pmUserName', header: 'PM' },
        { key: 'pmIncentiveRate', header: 'PMインセンティブ率', format: (v: unknown) => v ? `${(Number(v) * 100).toFixed(2)}%` : '' },
        { key: 'pmIncentiveAmount', header: 'PMインセンティブ額', format: (v: unknown) => v ? Number(v).toLocaleString() : '' },
        // プロモーターインセンティブ
        { key: 'promoterUserId', header: 'プロモーターID' },
        { key: 'promoterUserName', header: 'プロモーター' },
        { key: 'promoterIncentiveRate', header: 'プロモーターインセンティブ率', format: (v: unknown) => v ? `${(Number(v) * 100).toFixed(2)}%` : '' },
        { key: 'promoterIncentiveAmount', header: 'プロモーターインセンティブ額', format: (v: unknown) => v ? Number(v).toLocaleString() : '' },
        // 製品情報
        { key: 'products', header: '製品情報', format: (v: unknown) => v ? JSON.stringify(v) : '' },
        // システム情報
        { key: 'createdAt', header: '登録日', format: (v: unknown) => v ? new Date(v as string).toLocaleDateString('ja-JP') : '' },
      ];
      exportToCSV(allItems as unknown as Record<string, unknown>[], csvColumns as Parameters<typeof exportToCSV>[1], '工事一覧');
    } catch (error) {
      console.error('Failed to export CSV:', error);
      setToast({message: 'CSVエクスポートに失敗しました', type: 'error'});
    } finally {
      setExporting(false);
    }
  };

  // CSVインポート関連
  const [importing, setImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState<CSVImportRow[]>([]);
  const [importStats, setImportStats] = useState<{ update: number; create: number; error: number }>({ update: 0, create: 0, error: 0 });

  const handleImportCSV = async () => {
    try {
      const csvContent = await importCSVFile();

      const fileSizeBytes = new Blob([csvContent]).size;
      if (fileSizeBytes > 10 * 1024 * 1024) {
        setToast({message: 'ファイルサイズが10MBを超えています。ファイルを分割してください。', type: 'error'});
        return;
      }

      const parsedData = parseCSV<CSVImportRow>(csvContent, csvColumnMapping);
      
      if (parsedData.length === 0) {
        setToast({message: 'インポートするデータがありません', type: 'error'});
        return;
      }

      if (parsedData.length > 10000) {
        setToast({message: `データ行数が${parsedData.length}行あり、上限の10,000行を超えています。ファイルを分割してください。`, type: 'error'});
        return;
      }

      // ステータス・優先度・請求ステータスを変換
      const convertedData = parsedData.map(row => ({
        ...row,
        status: row.status ? (statusReverseMapping[row.status] || row.status) : undefined,
        priority: row.priority ? (priorityReverseMapping[row.priority] || row.priority) : undefined,
        invoiceStatus: row.invoiceStatus ? (invoiceStatusReverseMapping[row.invoiceStatus] || row.invoiceStatus) : undefined,
        // 金額のカンマを除去
        estimateAmount: row.estimateAmount?.replace(/,/g, ''),
        orderAmount: row.orderAmount?.replace(/,/g, ''),
        referralFee: row.referralFee?.replace(/,/g, ''),
        invoiceAmount: row.invoiceAmount?.replace(/,/g, ''),
        // 日付を変換（YYYY/MM/DD → YYYY-MM-DD）
        orderDate: convertDateFormat(row.orderDate),
        constructionDate: convertDateFormat(row.constructionDate),
        desiredDate: convertDateFormat(row.desiredDate),
        completionDate: convertDateFormat(row.completionDate),
        invoiceDate: convertDateFormat(row.invoiceDate),
        paymentDate: convertDateFormat(row.paymentDate),
      }));

      setImportPreviewData(convertedData);
      
      // 既存データとの突合（工事番号で判定）
      const existingNumbers = constructions.map(c => c.constructionNumber);
      let updateCount = 0;
      let createCount = 0;
      convertedData.forEach(row => {
        if (row.constructionNumber && existingNumbers.includes(row.constructionNumber)) {
          updateCount++;
        } else if (row.title) {
          createCount++;
        }
      });
      setImportStats({ update: updateCount, create: createCount, error: 0 });
      setShowImportModal(true);
    } catch (error) {
      console.error('Failed to parse CSV:', error);
      setToast({message: 'CSVファイルの読み込みに失敗しました', type: 'error'});
    }
  };

  // 日付フォーマット変換
  const convertDateFormat = (dateStr?: string): string | undefined => {
    if (!dateStr) return undefined;
    // YYYY/MM/DD or YYYY/M/D → YYYY-MM-DD
    const match = dateStr.match(/^(\d{4})[/\-](\d{1,2})[/\-](\d{1,2})$/);
    if (match) {
      const [, year, month, day] = match;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  };

  const handleImportConfirm = async () => {
    if (importing) return;
    setImporting(true);
    try {
      const result = await api.post<{ updated: number; created: number; errors: string[] }>('/api/constructions/import', {
        items: importPreviewData, // 企業ID・担当者IDはCSVから直接取得
      });
      
      const errorDetail = result.errors?.length > 0 ? `\nエラー: ${result.errors.length}件\n${result.errors.slice(0, 5).join('\n')}${result.errors.length > 5 ? `\n...他${result.errors.length - 5}件` : ''}` : '';
      setToast({message: `インポート完了\n更新: ${result.updated}件\n新規: ${result.created}件${errorDetail}`, type: result.errors?.length > 0 ? 'error' : 'success'});
      setShowImportModal(false);
      setImportPreviewData([]);
      fetchConstructions();
    } catch (error) {
      console.error('Failed to import CSV:', error);
      setToast({message: 'CSVインポートに失敗しました', type: 'error'});
    } finally {
      setImporting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // 需要場所マスタを選択した時に詳細情報を自動入力
  const handleDemandLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setFormData(prev => ({ ...prev, demandLocationId: selectedId }));
    
    if (selectedId) {
      const selected = demandLocations.find(d => d.id === selectedId);
      if (selected) {
        setFormData(prev => ({
          ...prev,
          demandLocationId: selectedId,
          demandLocationName: selected.name || prev.demandLocationName,
          demandLocationAddress: selected.address || prev.demandLocationAddress,
          demandLocationPostalCode: selected.postalCode || prev.demandLocationPostalCode,
          demandLocationContact: selected.contactPerson || prev.demandLocationContact,
          demandLocationPhone: selected.phone || prev.demandLocationPhone,
        }));
      }
    }
  };

  // 電力会社マスタを選択した時に詳細情報を自動入力
  const handlePowerCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setFormData(prev => ({ ...prev, powerCompanyId: selectedId }));
    
    if (selectedId) {
      const selected = powerCompanies.find(p => p.id === selectedId);
      if (selected) {
        setFormData(prev => ({
          ...prev,
          powerCompanyId: selectedId,
          powerCompanyName: selected.name || prev.powerCompanyName,
        }));
      }
    }
  };

  // 製品をマスタから追加
  const handleAddProductFromMaster = () => {
    if (!selectedProductId) return;
    
    const selected = products.find(p => p.id === selectedProductId);
    if (selected) {
      const newProduct: ConstructionProduct = {
        id: crypto.randomUUID(),
        productId: selected.id,
        productName: selected.name,
        productModel: selected.model || '',
        quantity: 1,
        unitPrice: selected.unitPrice || 0,
      };
      setFormProducts(prev => [...prev, newProduct]);
      setSelectedProductId('');
    }
  };

  // 製品を手動で追加
  const handleAddManualProduct = () => {
    const newProduct: ConstructionProduct = {
      id: crypto.randomUUID(),
      productName: '',
      productModel: '',
      quantity: 1,
      unitPrice: 0,
    };
    setFormProducts(prev => [...prev, newProduct]);
  };

  // 製品を更新
  const handleUpdateProduct = (id: string, field: keyof ConstructionProduct, value: string | number) => {
    setFormProducts(prev => prev.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  // 製品を削除
  const handleDeleteProduct = (id: string) => {
    setFormProducts(prev => prev.filter(p => p.id !== id));
  };

  // 製品合計金額を計算
  const calculateProductsTotal = () => {
    return formProducts.reduce((sum, p) => sum + (p.quantity || 0) * (p.unitPrice || 0), 0);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!formData.title.trim()) {
      setToast({message: 'タイトルを入力してください', type: 'error'});
      return;
    }
    if (!formData.companyId) {
      setToast({message: '企業を選択してください', type: 'error'});
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        // ID系: 空文字はnullに
        companyId: formData.companyId || null,
        areaId: formData.areaId || null,
        constructionTypeId: formData.constructionTypeId || null,
        contractTypeId: formData.contractTypeId || null,
        demandLocationId: formData.demandLocationId || null,
        powerCompanyId: formData.powerCompanyId || null,
        constructionPartnerId: formData.constructionPartnerId || null,
        assignedTo: formData.assignedTo || null,
        pmUserId: formData.pmUserId || null,
        promoterUserId: formData.promoterUserId || null,
        // 金額系: 数値変換
        estimateAmount: formData.estimateAmount ? parseFloat(formData.estimateAmount) : null,
        orderAmount: formData.orderAmount ? parseFloat(formData.orderAmount) : null,
        referralFee: formData.referralFee ? parseFloat(formData.referralFee) : null,
        referralFeePercentage: formData.referralFeePercentage ? parseFloat(formData.referralFeePercentage) : null,
        invoiceAmount: formData.invoiceAmount ? parseFloat(formData.invoiceAmount) : null,
        incentiveAmount: formData.incentiveAmount ? parseFloat(formData.incentiveAmount) : null,
        // インセンティブ率: 数値変換（パーセントから小数に）
        assignedToIncentiveRate: formData.assignedToIncentiveRate 
          ? parseFloat(formData.assignedToIncentiveRate) / 100 
          : incentiveSettings.staffRate,
        pmIncentiveRate: formData.pmIncentiveRate 
          ? parseFloat(formData.pmIncentiveRate) / 100 
          : incentiveSettings.pmRate,
        promoterIncentiveRate: formData.promoterIncentiveRate 
          ? parseFloat(formData.promoterIncentiveRate) / 100 
          : incentiveSettings.promoterRate,
        // 日付系: 空文字はnullに
        orderDate: formData.orderDate || null,
        constructionDate: formData.constructionDate || null,
        desiredDate: formData.desiredDate || null,
        invoiceDate: formData.invoiceDate || null,
        paymentDate: formData.paymentDate || null,
        incentivePaidDate: formData.incentivePaidDate || null,
        // 請求情報
        priorBilling: formData.priorBilling || null,
        billingDestination: formData.billingDestination || null,
        // 製品情報（配列）
        products: formProducts,
      };
      await api.post('/api/constructions', submitData);
      setShowModal(false);
      setFormData(initialFormData);
      setFormProducts([]);
      setActiveTab('basic');
      fetchConstructions();
    } catch (error) {
      console.error('Failed to create construction:', error);
      setToast({message: '工事の登録に失敗しました', type: 'error'});
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (construction: Construction, newStatus: string) => {
    if (loading) return;
    try {
      await api.put(`/api/constructions/${construction.id}`, { status: newStatus });
      fetchConstructions();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const formatCurrency = (amount: string | null) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(Number(amount));
  };

  const columns = [
    { key: 'constructionNumber', header: '工事番号', minWidth: '120px', sortable: true, mobileTitle: true, render: (con: Construction) => (
      <Link to={`/constructions/${con?.id || ''}`} className="text-blue-600 hover:underline font-medium">
        {con?.constructionNumber || '-'}
        <span className="block text-xs text-gray-500 md:hidden truncate">{con?.title || '-'}</span>
      </Link>
    )},
    { key: 'title', header: 'タイトル', minWidth: '200px', sortable: true, hideOnMobile: true, render: (con: Construction) => (
      <Link to={`/constructions/${con?.id || ''}`} className="text-gray-900 dark:text-white hover:text-blue-600 line-clamp-1">
        {con?.title || '-'}
      </Link>
    )},
    { key: 'companyName', header: '企業名', minWidth: '120px', sortable: true, render: (con: Construction) => con?.companyName || '-' },
    { key: 'status', header: 'ステータス', minWidth: '100px', sortable: true, render: (con: Construction) => (
      <select
        value={con?.status || 'pending'}
        onChange={(e) => {
          e.stopPropagation();
          handleStatusChange(con, e.target.value);
        }}
        className="text-xs bg-transparent border-none cursor-pointer w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <option value="pending">準備中</option>
        <option value="in_progress">進行中</option>
        <option value="completed">完了</option>
        <option value="cancelled">キャンセル</option>
      </select>
    )},
    { key: 'estimateAmount', header: '見積', minWidth: '100px', sortable: true, hideOnMobile: true, render: (con: Construction) => formatCurrency(con?.estimateAmount ?? null) },
    { key: 'orderAmount', header: '受注', minWidth: '100px', sortable: true, render: (con: Construction) => formatCurrency(con?.orderAmount ?? null) },
    { key: 'orderDate', header: '受注日', minWidth: '90px', sortable: true, hideOnMobile: true, render: (con: Construction) => 
      con?.orderDate ? new Date(con.orderDate).toLocaleDateString('ja-JP') : '-'
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">工事管理</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleExportCSV} disabled={exporting} className="w-full sm:w-auto">
            {exporting ? (
              <>
                <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                エクスポート中...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                CSVエクスポート
              </>
            )}
          </Button>
          {isPromoter && (
            <Button variant="secondary" onClick={handleImportCSV} className="w-full sm:w-auto">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              CSVインポート
            </Button>
          )}
          {canCreateConstruction && (
            <Button onClick={() => setShowModal(true)} className="w-full sm:w-auto">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              新規工事
            </Button>
          )}
        </div>
      </div>

      <Card>
        {/* 詳細検索 */}
        <div className="mb-4">
          <AdvancedSearch
            fields={searchFields}
            onSearch={handleSearch}
            quickSearchPlaceholder="工事番号・タイトルで検索..."
            quickSearchField="title"
            initialConditions={searchConditions}
          />
        </div>
        
        <Table
          columns={columns}
          data={constructions}
          loading={loading}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        
        {/* ページネーション */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
            <span className="hidden sm:inline">表示件数:</span>
            <Select
              value={pageSize.toString()}
              onChange={(e) => {
                setSearchParams(prev => {
                  prev.set('pageSize', e.target.value);
                  prev.delete('page');
                  return prev;
                });
              }}
              className="w-20 sm:w-24 text-sm"
            >
              <option value="10">10件</option>
              <option value="30">30件</option>
              <option value="50">50件</option>
              <option value="100">100件</option>
            </Select>
          </div>
          <div className="order-1 sm:order-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(newPage) => {
                setSearchParams(prev => {
                  prev.set('page', String(newPage));
                  return prev;
                });
              }}
            />
          </div>
        </div>
      </Card>

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setFormData(initialFormData); setActiveTab('basic'); }} title="新規工事" size="xl">
        <form onSubmit={handleCreate} className="space-y-4">
          {/* タブナビゲーション */}
          <div className="flex gap-1 border-b dark:border-gray-700 overflow-x-auto">
            {[
              { id: 'basic', label: '基本情報' },
              { id: 'demand', label: '需要場所' },
              { id: 'power', label: '電力会社' },
              { id: 'invoice', label: '請求情報' },
              { id: 'product', label: '製品情報' },
              ...(isPromoter ? [{ id: 'incentive', label: 'インセンティブ' }] : []),
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* 基本情報タブ */}
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <Input label="タイトル" name="title" value={formData.title} onChange={handleChange} required />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">説明</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    企業 <span className="text-red-500">*</span>
                  </label>
                  <Select name="companyId" value={formData.companyId} onChange={handleChange} required>
                    <option value="">選択してください</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">エリア</label>
                  <Select name="areaId" value={formData.areaId} onChange={handleChange}>
                    <option value="">選択してください</option>
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">工事種別</label>
                  <Select name="constructionTypeId" value={formData.constructionTypeId} onChange={handleChange}>
                    <option value="">選択してください</option>
                    {constructionTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">契約種別</label>
                  <Select name="contractTypeId" value={formData.contractTypeId} onChange={handleChange}>
                    <option value="">選択してください</option>
                    {contractTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ステータス</label>
                  <Select name="status" value={formData.status} onChange={handleChange}>
                    <option value="pending">準備中</option>
                    <option value="in_progress">進行中</option>
                    <option value="completed">完了</option>
                    <option value="cancelled">キャンセル</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">優先度</label>
                  <Select name="priority" value={formData.priority} onChange={handleChange}>
                    <option value="low">低</option>
                    <option value="normal">通常</option>
                    <option value="high">高</option>
                    <option value="urgent">緊急</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">担当者</label>
                  <Select name="assignedTo" value={formData.assignedTo} onChange={handleChange}>
                    <option value="">未割当</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="border-t dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">金額情報</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="見積金額" name="estimateAmount" type="number" min={0} value={formData.estimateAmount} onChange={handleChange} />
                  <Input label="受注金額" name="orderAmount" type="number" min={0} value={formData.orderAmount} onChange={handleChange} />
                  <Input label="紹介料" name="referralFee" type="number" min={0} value={formData.referralFee} onChange={handleChange} />
                  <Input label="紹介料率(%)" name="referralFeePercentage" type="number" step="0.1" min={0} value={formData.referralFeePercentage} onChange={handleChange} />
                </div>
              </div>
              <div className="border-t dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">日程情報</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Input label="受注日" name="orderDate" type="date" value={formData.orderDate} onChange={handleChange} />
                  <Input label="施工日" name="constructionDate" type="date" value={formData.constructionDate} onChange={handleChange} />
                  <Input label="工事希望日" name="desiredDate" type="date" value={formData.desiredDate} onChange={handleChange} />
                </div>
              </div>
            </div>
          )}

          {/* 需要場所タブ */}
          {activeTab === 'demand' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">需要場所マスタから選択</label>
                <Select name="demandLocationId" value={formData.demandLocationId} onChange={handleDemandLocationChange}>
                  <option value="">選択してください（または下記に直接入力）</option>
                  {demandLocations.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </Select>
              </div>
              <div className="border-t dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">需要場所情報（直接入力）</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="名称" name="demandLocationName" value={formData.demandLocationName} onChange={handleChange} />
                  <Input label="郵便番号" name="demandLocationPostalCode" value={formData.demandLocationPostalCode} onChange={handleChange} />
                  <div className="sm:col-span-2">
                    <Input label="住所" name="demandLocationAddress" value={formData.demandLocationAddress} onChange={handleChange} />
                  </div>
                  <Input label="担当者" name="demandLocationContact" value={formData.demandLocationContact} onChange={handleChange} />
                  <Input label="電話番号" name="demandLocationPhone" value={formData.demandLocationPhone} onChange={handleChange} />
                </div>
              </div>
            </div>
          )}

          {/* 電力会社タブ */}
          {activeTab === 'power' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">電力会社マスタから選択</label>
                <Select name="powerCompanyId" value={formData.powerCompanyId} onChange={handlePowerCompanyChange}>
                  <option value="">選択してください（または下記に直接入力）</option>
                  {powerCompanies.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Select>
              </div>
              <div className="border-t dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">電力会社情報（直接入力）</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="電力会社名" name="powerCompanyName" value={formData.powerCompanyName} onChange={handleChange} />
                  <Input label="支店名" name="powerCompanyBranch" value={formData.powerCompanyBranch} onChange={handleChange} />
                  <Input label="お客様番号" name="customerNumber" value={formData.customerNumber} onChange={handleChange} />
                  <Input label="供給地点番号" name="supplyNumber" value={formData.supplyNumber} onChange={handleChange} />
                </div>
              </div>
            </div>
          )}

          {/* 請求情報タブ */}
          {activeTab === 'invoice' && (
            <div className="space-y-4">
              {/* 施工パートナー選択と請求先 */}
              <div className="border-b dark:border-gray-700 pb-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">施工パートナー・請求先</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">施工パートナー</label>
                    <Select 
                      name="constructionPartnerId" 
                      value={formData.constructionPartnerId} 
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        setFormData(prev => ({ ...prev, constructionPartnerId: selectedId }));
                        // 施工パートナーを選択時、請求先に自動入力
                        if (selectedId) {
                          const selected = constructionPartners.find(p => p.id === selectedId);
                          if (selected) {
                            const billingInfo = [
                              selected.name,
                              selected.postalCode ? `〒${selected.postalCode}` : '',
                              selected.address || '',
                              selected.phone ? `TEL: ${selected.phone}` : '',
                              selected.contactPerson ? `担当: ${selected.contactPerson}` : '',
                            ].filter(Boolean).join('\n');
                            setFormData(prev => ({
                              ...prev,
                              constructionPartnerId: selectedId,
                              billingDestination: billingInfo,
                            }));
                          }
                        }
                      }}
                    >
                      <option value="">選択してください</option>
                      {constructionPartners.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </Select>
                  </div>
                  <Input label="前請求の有無" name="priorBilling" value={formData.priorBilling} onChange={handleChange} placeholder="例: あり、なし、一部前請など" />
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    請求先
                    <span className="text-xs text-gray-500 ml-2">※施工パートナー選択で自動入力されます（編集可）</span>
                  </label>
                  <textarea
                    name="billingDestination"
                    value={formData.billingDestination}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder="請求先の名称、住所、連絡先などを入力"
                  />
                </div>
              </div>

              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">請求情報</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="請求書番号" name="invoiceNumber" value={formData.invoiceNumber} onChange={handleChange} />
                <Input label="請求日" name="invoiceDate" type="date" value={formData.invoiceDate} onChange={handleChange} />
                  <Input label="請求金額" name="invoiceAmount" type="number" min={0} value={formData.invoiceAmount} onChange={handleChange} />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">請求ステータス</label>
                  <Select name="invoiceStatus" value={formData.invoiceStatus} onChange={handleChange}>
                    <option value="not_issued">未発行</option>
                    <option value="issued">発行済</option>
                    <option value="paid">入金済</option>
                  </Select>
                </div>
                <Input label="入金日" name="paymentDate" type="date" value={formData.paymentDate} onChange={handleChange} />
              </div>
              <div className="border-t dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">インセンティブ</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="インセンティブ金額" name="incentiveAmount" type="number" min={0} value={formData.incentiveAmount} onChange={handleChange} />
                  <div className="flex items-center gap-2 pt-6">
                    <input
                      type="checkbox"
                      id="incentivePaid"
                      name="incentivePaid"
                      checked={formData.incentivePaid}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label htmlFor="incentivePaid" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      支払済み
                    </label>
                  </div>
                  <Input label="支払日" name="incentivePaidDate" type="date" value={formData.incentivePaidDate} onChange={handleChange} />
                </div>
              </div>
            </div>
          )}

          {/* 製品情報タブ */}
          {activeTab === 'product' && (
            <div className="space-y-4">
              {/* 製品追加エリア */}
              <div className="flex flex-col sm:flex-row gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">製品マスタから追加</label>
                  <div className="flex gap-2">
                    <Select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)} className="flex-1">
                      <option value="">製品を選択してください</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}{p.model ? ` (${p.model})` : ''}</option>
                      ))}
                    </Select>
                    <Button type="button" onClick={handleAddProductFromMaster} disabled={!selectedProductId}>
                      追加
                    </Button>
                  </div>
                </div>
                <div className="sm:pt-6">
                  <Button type="button" variant="secondary" onClick={handleAddManualProduct}>
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    手動追加
                  </Button>
                </div>
              </div>

              {/* 製品一覧 */}
              {formProducts.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <p>製品が追加されていません</p>
                  <p className="text-sm">上のボタンから製品を追加してください</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {formProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onUpdate={handleUpdateProduct}
                      onDelete={handleDeleteProduct}
                    />
                  ))}
                </div>
              )}

              {/* 合計金額 */}
              {formProducts.length > 0 && (
                <div className="flex justify-end pt-4 border-t dark:border-gray-700">
                  <div className="text-right">
                    <span className="text-sm text-gray-500 dark:text-gray-400">製品合計金額</span>
                    <p className="text-2xl font-bold text-blue-600">
                      {new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(calculateProductsTotal())}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* インセンティブタブ（管理者・プロモーターのみ） */}
          {activeTab === 'incentive' && isPromoter && (
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  紹介手数料にインセンティブ率を掛けて各担当者のインセンティブ額が自動計算されます。
                  デフォルトの率は設定から取得されますが、工事ごとに変更できます。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* 担当者インセンティブ */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">担当者</span>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">担当者（基本情報から）</label>
                    <Select name="assignedTo" value={formData.assignedTo} onChange={handleChange}>
                      <option value="">未選択</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">インセンティブ率 (%)</label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        name="assignedToIncentiveRate"
                        value={formData.assignedToIncentiveRate || (incentiveSettings.staffRate * 100).toFixed(2)}
                        onChange={(e) => setFormData(prev => ({ ...prev, assignedToIncentiveRate: e.target.value }))}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                    </div>
                  </div>
                  {formData.referralFee && (
                    <div className="mt-2 text-right">
                      <span className="text-xs text-gray-500">インセンティブ額: </span>
                      <span className="text-sm font-semibold text-green-600">
                        ¥{Math.round(parseFloat(formData.referralFee) * (parseFloat(formData.assignedToIncentiveRate || String(incentiveSettings.staffRate * 100)) / 100)).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* PMインセンティブ */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">PM</span>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">PMを選択</label>
                    <Select name="pmUserId" value={formData.pmUserId} onChange={handleChange}>
                      <option value="">未選択</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">インセンティブ率 (%)</label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        name="pmIncentiveRate"
                        value={formData.pmIncentiveRate || (incentiveSettings.pmRate * 100).toFixed(2)}
                        onChange={(e) => setFormData(prev => ({ ...prev, pmIncentiveRate: e.target.value }))}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                    </div>
                  </div>
                  {formData.referralFee && (
                    <div className="mt-2 text-right">
                      <span className="text-xs text-gray-500">インセンティブ額: </span>
                      <span className="text-sm font-semibold text-purple-600">
                        ¥{Math.round(parseFloat(formData.referralFee) * (parseFloat(formData.pmIncentiveRate || String(incentiveSettings.pmRate * 100)) / 100)).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* プロモーターインセンティブ */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">プロモーター</span>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">プロモーターを選択</label>
                    <Select name="promoterUserId" value={formData.promoterUserId} onChange={handleChange}>
                      <option value="">未選択</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">インセンティブ率 (%)</label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        name="promoterIncentiveRate"
                        value={formData.promoterIncentiveRate || (incentiveSettings.promoterRate * 100).toFixed(2)}
                        onChange={(e) => setFormData(prev => ({ ...prev, promoterIncentiveRate: e.target.value }))}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                    </div>
                  </div>
                  {formData.referralFee && (
                    <div className="mt-2 text-right">
                      <span className="text-xs text-gray-500">インセンティブ額: </span>
                      <span className="text-sm font-semibold text-orange-600">
                        ¥{Math.round(parseFloat(formData.referralFee) * (parseFloat(formData.promoterIncentiveRate || String(incentiveSettings.promoterRate * 100)) / 100)).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* 紹介手数料入力と合計 */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">紹介手数料</label>
                    <Input
                      type="number"
                      name="referralFee"
                      value={formData.referralFee}
                      onChange={handleChange}
                      placeholder="例: 100000"
                    />
                  </div>
                  {formData.referralFee && (
                    <div className="flex items-end justify-end">
                      <div className="text-right">
                        <span className="text-sm text-gray-500">合計インセンティブ額</span>
                        <p className="text-2xl font-bold text-blue-600">
                          ¥{(() => {
                            const fee = parseFloat(formData.referralFee) || 0;
                            const staffRate = parseFloat(formData.assignedToIncentiveRate || String(incentiveSettings.staffRate * 100)) / 100;
                            const pmRate = parseFloat(formData.pmIncentiveRate || String(incentiveSettings.pmRate * 100)) / 100;
                            const promoterRate = parseFloat(formData.promoterIncentiveRate || String(incentiveSettings.promoterRate * 100)) / 100;
                            return Math.round(fee * (staffRate + pmRate + promoterRate)).toLocaleString();
                          })()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4 border-t dark:border-gray-700">
            <Button type="button" variant="secondary" onClick={() => { setShowModal(false); setFormData(initialFormData); setFormProducts([]); setActiveTab('basic'); }} className="w-full sm:w-auto">
              キャンセル
            </Button>
            <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? '登録中...' : '登録'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* CSVインポートモーダル */}
      <Modal isOpen={showImportModal} onClose={() => { setShowImportModal(false); setImportPreviewData([]); }} title="CSVインポート" size="xl">
        <div className="space-y-4">
          {/* インポート概要 */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2">インポート概要</h4>
            <div className="flex gap-6 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">更新: </span>
                <span className="font-bold text-orange-600">{importStats.update}件</span>
                <span className="text-xs text-gray-500 ml-1">（工事番号で既存データを更新）</span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">新規: </span>
                <span className="font-bold text-green-600">{importStats.create}件</span>
              </div>
            </div>
          </div>

          {/* プレビューテーブル */}
          <div className="max-h-96 overflow-auto border dark:border-gray-700 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">工事番号</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">タイトル</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">企業ID</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">ステータス</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">見積金額</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">受注金額</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {importPreviewData.slice(0, 50).map((row, index) => {
                  const isUpdate = row.constructionNumber && constructions.some(c => c.constructionNumber === row.constructionNumber);
                  return (
                    <tr key={index} className={isUpdate ? 'bg-orange-50 dark:bg-orange-900/10' : 'bg-green-50 dark:bg-green-900/10'}>
                      <td className="px-3 py-2">
                        <Badge variant={isUpdate ? 'warning' : 'success'}>
                          {isUpdate ? '更新' : '新規'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{row.constructionNumber || '-'}</td>
                      <td className="px-3 py-2 max-w-xs truncate">{row.title || '-'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{row.companyId || '-'}</td>
                      <td className="px-3 py-2">{row.status || '-'}</td>
                      <td className="px-3 py-2 text-right">{row.estimateAmount || '-'}</td>
                      <td className="px-3 py-2 text-right">{row.orderAmount || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {importPreviewData.length > 50 && (
              <div className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-center text-sm text-gray-500">
                ...他 {importPreviewData.length - 50} 件
              </div>
            )}
          </div>

          {/* 注意事項 */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm">
            <p className="text-yellow-700 dark:text-yellow-300">
              <strong>注意:</strong> 工事番号が一致するデータは上書き更新されます。工事番号がないデータは新規登録されます。
              <br />
              <span className="text-xs">※ 企業ID・担当者IDでデータを更新します。企業名・担当者名はエクスポート時の参照用です。</span>
            </p>
          </div>

          {/* ボタン */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4 border-t dark:border-gray-700">
            <Button type="button" variant="secondary" onClick={() => { setShowImportModal(false); setImportPreviewData([]); }} className="w-full sm:w-auto">
              キャンセル
            </Button>
            <Button onClick={handleImportConfirm} disabled={importing} className="w-full sm:w-auto">
              {importing ? (
                <>
                  <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  インポート中...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  インポート実行
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
